import { WarningOutlined } from '@ant-design/icons';
import { Modal, Button, Typography } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { authApi } from '../services/authApi';
import settingsApi from '../services/settingsApi';

// If jwt-decode types are missing, install them with: npm install --save-dev @types/jwt-decode

// dayjs plugin'lerini yükle
dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;

interface User {
  id: string;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  roles: string[];
  userGroupId?: string;
  userGroupName?: string;
}

interface SessionSettings {
  sessionTimeoutMinutes: number;
  rememberMe: boolean;
  autoRefreshSession: boolean;
  deviceInfo?: string;
  lastIpAddress?: string;
  lastLoginAt?: string;
  timeZone?: string;
  useSystemTimeZone?: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  sessionTimeoutMinutes: number;
  setSessionTimeoutMinutes: (minutes: number) => Promise<void>;
  remainingSessionTime: number | null;
  showTimeoutWarning: boolean;
  timeoutCountdown: number;
  extendSession: () => void;
  sessionSettings: SessionSettings;
  setSessionSettings: (settings: Partial<SessionSettings>) => Promise<void>;
  userTimeZone: string;
  getUserTimeZone: () => string;
  formatDateForUser: (date: Date | string | number) => string;
  formatDateTimeForUser: (date: Date | string | number) => string;
  login: (credentials: { username: string, password: string, rememberMe?: boolean }) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  apiUrl: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Varsayılan oturum süresi (dakika)
const DEFAULT_SESSION_TIMEOUT = 60;

// localStorage'dan oturum süresini al veya varsayılan değeri kullan
const getStoredSessionTimeout = (): number => {
  const stored = localStorage.getItem('sessionTimeoutMinutes');
  return stored ? parseInt(stored, 10) : DEFAULT_SESSION_TIMEOUT;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionTimeoutMinutesState, setSessionTimeoutMinutesState] = useState<number>(DEFAULT_SESSION_TIMEOUT);
  const [remainingSessionTime, setRemainingSessionTime] = useState<number | null>(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState<boolean>(false);
  const [timeoutCountdown, setTimeoutCountdown] = useState<number>(30);
  const [sessionSettingsState, setSessionSettingsState] = useState<SessionSettings>({
    sessionTimeoutMinutes: DEFAULT_SESSION_TIMEOUT,
    rememberMe: false,
    autoRefreshSession: false
  });
  const [sessionCheckInterval, setSessionCheckInterval] = useState<NodeJS.Timeout | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://localhost:7263';

  // Forward declarations for functions used in useEffects
  const fetchUserData = async () => {
    try {
      console.log('AuthContext: Fetching user data');
      setLoading(true);
      
      const userData = await authApi.getCurrentUser();
      
      if (userData) {
        console.log('AuthContext: User data received successfully');
        // Roller: string | string[] gelebilir, hepsini lowercase string[]'e normalize et
        const rawRoles = (userData as any).roles ?? [];
        const rolesArray: string[] = Array.isArray(rawRoles)
          ? rawRoles
          : (typeof rawRoles === 'string' && rawRoles.length > 0 ? [rawRoles] : []);
        const normalizedRoles = rolesArray.map(r => String(r).toLowerCase());
        setUser({ ...(userData as any), roles: normalizedRoles });
        setIsAuthenticated(true);
        // window.isAuthenticated'i senkronize et
        (window as any).isAuthenticated = true;
      } else {
        console.log('AuthContext: No user data received');
        setUser(null);
        setIsAuthenticated(false);
        // window.isAuthenticated'i senkronize et
        (window as any).isAuthenticated = false;
      }
    } catch (error) {
      console.error('AuthContext: Failed to fetch user data:', error);
      setError('Kullanıcı bilgileri alınamadı');
      setUser(null);
      setIsAuthenticated(false);
      // window.isAuthenticated'i senkronize et
      (window as any).isAuthenticated = false;
    } finally {
      setLoading(false);
    }
  };
  
  // Kullanıcı ayarlarından oturum süresini al
  const fetchSessionTimeout = async () => {
    try {
      console.log('AuthContext: Fetching session timeout from user settings');
      const timeoutMinutes = await settingsApi.getSessionTimeoutSetting();
      
      console.log(`AuthContext: Session timeout from user settings: ${timeoutMinutes} minutes`);
      setSessionTimeoutMinutesState(timeoutMinutes || DEFAULT_SESSION_TIMEOUT);
      
      // Mevcut session settings'i güncelle
      setSessionSettingsState(prevSettings => ({
        ...prevSettings,
        sessionTimeoutMinutes: timeoutMinutes || DEFAULT_SESSION_TIMEOUT
      }));
      
      return timeoutMinutes;
    } catch (error) {
      console.error('AuthContext: Failed to fetch session timeout from user settings:', error);
      return DEFAULT_SESSION_TIMEOUT;
    }
  };
  
  const fetchSessionSettings = async () => {
    try {
      console.log('AuthContext: Fetching session settings');
      const settings = await authApi.getSessionSettings();
      
      // Önce API'den gelen ayarları kullan
      if (settings) {
        console.log('AuthContext: Session settings received from API:', settings);
        setSessionSettingsState(settings);
        
        // Kullanıcı ayarlarından oturum süresini al ve API'den gelen ayarları güncelle
        const userTimeout = await fetchSessionTimeout();
        
        // Eğer kullanıcı ayarlarından bir süre geldiyse, API'den gelen süreyi geçersiz kıl
        if (userTimeout) {
          setSessionTimeoutMinutesState(userTimeout);
        } else {
          // Yoksa API'den gelen süreyi kullan
          setSessionTimeoutMinutesState(settings.sessionTimeoutMinutes || DEFAULT_SESSION_TIMEOUT);
        }
      } else {
        console.log('AuthContext: No session settings received from API, using user settings');
        // API'den ayar gelmezse kullanıcı ayarlarını kontrol et
        await fetchSessionTimeout();
      }
    } catch (error) {
      console.error('AuthContext: Failed to fetch session settings from API:', error);
      // API hata verirse kullanıcı ayarlarını kontrol et
      await fetchSessionTimeout();
    }
  };
  
  // Memoized callbacks for fetchUserData and fetchSessionSettings
  const memoizedFetchUserData = useCallback(fetchUserData, []);
  const memoizedFetchSessionSettings = useCallback(fetchSessionSettings, []);

  // Oturum ayarlarını güncelle
  const setSessionSettings = useCallback(async (settings: Partial<SessionSettings>): Promise<void> => {
    try {
      console.log('AuthContext: Updating session settings', settings);
      
      const completeSettings = {
        ...sessionSettingsState,
        ...settings
      };
      
      await authApi.saveSessionSettings(completeSettings);
      
      setSessionSettingsState(prev => ({
        ...prev,
        ...settings
      }));
      
      console.log('AuthContext: Session settings updated successfully');
    } catch (error) {
      console.error('AuthContext: Failed to update session settings:', error);
      toast.error('Oturum ayarları güncellenirken bir hata oluştu');
    }
  }, [sessionSettingsState]);

  // Kullanıcının zaman dilimini al
  const getUserTimeZone = useCallback(() => {
    if (sessionSettingsState.useSystemTimeZone) {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }
    return sessionSettingsState.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }, [sessionSettingsState.useSystemTimeZone, sessionSettingsState.timeZone]);

  // Tarih formatla (sadece tarih)
  const formatDateForUser = useCallback((date: Date | string | number) => {
    const timeZone = getUserTimeZone();
    return dayjs(date).tz(timeZone).format('DD.MM.YYYY');
  }, [getUserTimeZone]);

  // Tarih ve saat formatla
  const formatDateTimeForUser = useCallback((date: Date | string | number) => {
    const timeZone = getUserTimeZone();
    return dayjs(date).tz(timeZone).format('DD.MM.YYYY HH:mm:ss');
  }, [getUserTimeZone]);

  // Oturum süresini ayarla
  const setSessionTimeoutMinutes = useCallback(async (minutes: number): Promise<void> => {
    console.log(`AuthContext: Setting session timeout to ${minutes} minutes`);
    
    // State'i güncelle
    setSessionTimeoutMinutesState(minutes);
    
    // Kullanıcı ayarlarına kaydet
    try {
      const success = await settingsApi.saveSessionTimeoutSetting(minutes);
      if (success) {
        console.log(`AuthContext: Session timeout saved to user settings: ${minutes} minutes`);
        
        // Mevcut session settings'i güncelle
        setSessionSettingsState(prevSettings => ({
          ...prevSettings,
          sessionTimeoutMinutes: minutes
        }));
        
        // API'ye de gönder
        try {
          await authApi.saveSessionSettings({ 
            sessionTimeoutMinutes: minutes, 
            rememberMe: sessionSettingsState.rememberMe, 
            autoRefreshSession: sessionSettingsState.autoRefreshSession
          });
          console.log(`AuthContext: Session timeout updated to ${minutes} minutes`);
          toast.success(`Oturum süresi ${minutes} dakika olarak güncellendi`);
        } catch (apiError) {
          console.error('AuthContext: Failed to update session settings in API:', apiError);
          // Kullanıcı ayarlarına kaydedildiği için hata göstermeye gerek yok
        }
      }
    } catch (error) {
      console.error('AuthContext: Failed to update session timeout:', error);
      toast.error('Oturum süresi güncellenirken bir hata oluştu');
    }
  }, [sessionSettingsState.rememberMe, sessionSettingsState.autoRefreshSession]);

  // Login fonksiyonu
  const login = useCallback(async (credentials: { username: string, password: string, rememberMe?: boolean }) => {
    const { username, password, rememberMe = false } = credentials;
    try {
      setLoading(true);
      setError(null);
      
      console.log(`AuthContext: Attempting login for user: ${username} with rememberMe: ${rememberMe}`);
      
      // API'ye login isteği gönder
      const response = await authApi.login({ username, password, rememberMe });
      
      console.log('AuthContext: Login API response received:', { 
        hasToken: Boolean(response.token), 
        tokenLength: response.token?.length || 0,
        rememberMe: response.rememberMe
      });
      
      // Beni hatırla seçeneğine göre token'ları sakla (API'den dönen değeri kullan)
      if (response.rememberMe) {
        console.log('AuthContext: Storing tokens in localStorage (rememberMe=true)');
        localStorage.setItem('accessToken', response.token);
        localStorage.setItem('refreshToken', response.refreshToken);
        localStorage.setItem('token', response.token);
        localStorage.setItem('rememberMe', 'true');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('token');
      } else {
        console.log('AuthContext: Storing tokens in sessionStorage (rememberMe=false)');
        sessionStorage.setItem('accessToken', response.token);
        sessionStorage.setItem('refreshToken', response.refreshToken);
        sessionStorage.setItem('token', response.token);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('token');
        localStorage.removeItem('rememberMe');
      }
      
      // Oturum ayarlarını güncelle
      setSessionSettingsState({
        ...sessionSettingsState,
        rememberMe: response.rememberMe,
        lastLoginAt: new Date().toISOString()
      });  
      
      console.log('AuthContext: Fetching user data after successful login');
      try {
        await memoizedFetchUserData();
        console.log('AuthContext: User data fetched successfully after login');
      } catch (userDataError: any) {
        // Kullanıcı bilgisi alınamazsa bile login başarılı sayılır
        // Sadece hata mesajı gösterilir ama token'lar korunur
        console.error('AuthContext: Failed to fetch user data after login:', userDataError);
        setError('Giriş başarılı ancak kullanıcı bilgileri alınamadı. Lütfen sayfayı yenileyin.');
      }
      
      setIsAuthenticated(true);
      setError(null);
      
      // Son aktivite zamanını ayarla
      const now = Date.now().toString();
      if (response.rememberMe) {
        localStorage.setItem('lastActivity', now);
      } else {
        sessionStorage.setItem('lastActivity', now);
      }
      
      console.log(`AuthContext: Login completed successfully, lastActivity set to: ${now}`);
    } catch (error: any) {
      console.error('AuthContext: Login failed:', error);
      
      // Token ve kullanıcı bilgilerini temizle
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
      setIsAuthenticated(false);
      setUser(null);
      // window.isAuthenticated'i senkronize et
      (window as any).isAuthenticated = false;
      
      // Hata mesajını ayarla
      setError(error instanceof Error ? error.message : 'Giriş başarısız');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [sessionSettingsState, memoizedFetchUserData]);

  // Oturumu uzat fonksiyonu
  const extendSession = useCallback(() => {
    console.log('AuthContext: Extending session');
    
    // Son aktivite zamanını güncelle
    const isRememberMe = localStorage.getItem('rememberMe') === 'true';
    const storage = isRememberMe ? localStorage : sessionStorage;
    const now = Date.now().toString();
    storage.setItem('lastActivity', now);
    
    // Uyarıyı kapat
    setShowTimeoutWarning(false);
    setTimeoutCountdown(30);
    
    // Otomatik yenileme aktifse oturumu yenile
    if (isAuthenticated && sessionSettingsState.autoRefreshSession) {
      console.log('AuthContext: Auto refresh is enabled, refreshing session');
      refreshSession();
    }
  }, [isAuthenticated, sessionSettingsState.autoRefreshSession]);

  // Logout fonksiyonu
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      console.log('AuthContext: Logging out');
      
      // API'ye logout isteği gönder
      try {
        await authApi.logout();
        console.log('AuthContext: Logout API call successful');
      } catch (logoutError) {
        console.error('AuthContext: Logout API call failed:', logoutError);
        // API hatası olsa bile devam et ve token'ları temizle
      }
      
      // Token'ları temizle
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('token');
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('lastActivity');
      
      sessionStorage.removeItem('accessToken');
      sessionStorage.removeItem('refreshToken');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('lastActivity');
      
      // State'i temizle
      setUser(null);
      setIsAuthenticated(false);
      // window.isAuthenticated'i senkronize et
      (window as any).isAuthenticated = false;
      
      console.log('AuthContext: Logout completed successfully');
      
      // Sayfayı yenile
      window.location.reload();
    } catch (error) {
      console.error('AuthContext: Logout failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);


  
  // İlk yükleme ve token kontrolü
  useEffect(() => {
    const checkInitialAuth = async () => {
      try {
        setIsLoading(true);
        console.log('AuthContext: Checking initial authentication');
        
        // Token kontrolü (yalnızca 'token')
        const hasLocalToken = Boolean(localStorage.getItem('token'));
        const hasSessionToken = Boolean(sessionStorage.getItem('token'));

        console.log('AuthContext: Initial token check', { hasLocalToken, hasSessionToken });

        if (!hasLocalToken && !hasSessionToken) {
          console.log('AuthContext: No tokens found, not authenticated');
          setIsAuthenticated(false);
          setUser(null);
          setIsLoading(false);
          // window.isAuthenticated'i senkronize et
          (window as any).isAuthenticated = false;
          return;
        }
        
        // Kullanıcı bilgilerini al
        await memoizedFetchUserData();
        
        // Oturum ayarlarını al
        await memoizedFetchSessionSettings();
        
        console.log('AuthContext: Initial authentication check completed');
      } catch (error) {
        console.error('AuthContext: Initial authentication check failed:', error);
        setIsAuthenticated(false);
        setUser(null);
        // window.isAuthenticated'i senkronize et
        (window as any).isAuthenticated = false;
      } finally {
        setIsLoading(false);
      }
    };
    
    checkInitialAuth();
  }, [memoizedFetchUserData, memoizedFetchSessionSettings]);

  // Session refresh fonksiyonu
  const refreshSession = async () => {
    try {
      setLoading(true);
      console.log('AuthContext: Refreshing session');
      
      // API'ye refresh token isteği gönder
      const response = await authApi.refreshSession();
      
      console.log('AuthContext: Session refresh API response received');
      
      if (response && response.token) {
        // Token'ları güncelle
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        console.log('AuthContext: Updating tokens', {
          rememberMe,
          storage: rememberMe ? 'localStorage' : 'sessionStorage'
        });
        
        if (rememberMe) {
          localStorage.setItem('accessToken', response.token);
          localStorage.setItem('refreshToken', response.refreshToken);
          localStorage.setItem('token', response.token);
        } else {
          sessionStorage.setItem('accessToken', response.token);
          sessionStorage.setItem('refreshToken', response.refreshToken);
          sessionStorage.setItem('token', response.token);
        }
        
        console.log('AuthContext: Tokens updated successfully');
      }
      
      // Oturum ayarlarını yeniden al, ancak sadece gerekiyorsa
      // Sonsuz döngüyü önlemek için bir bayrak kullan
      const shouldFetchSettings = localStorage.getItem('shouldFetchSessionSettings') !== 'false';
      if (shouldFetchSettings) {
        console.log('AuthContext: Fetching session settings after refresh');
        // Geçici olarak bayrağı false yap
        localStorage.setItem('shouldFetchSessionSettings', 'false');
        await fetchSessionSettings();
        // İşlem tamamlandıktan sonra bayrağı tekrar true yap
        setTimeout(() => {
          localStorage.setItem('shouldFetchSessionSettings', 'true');
        }, 5000); // 5 saniye bekle
      }
      
      // Oturum süresini güncelle - lastActivity timestamp'ini güncelle
      const storage = localStorage.getItem('rememberMe') === 'true' ? localStorage : sessionStorage;
      const now = Date.now().toString();
      storage.setItem('lastActivity', now);
      
      console.log('AuthContext: Session refresh completed successfully', {
        lastActivity: new Date(parseInt(now)).toLocaleString(),
        storage: storage === localStorage ? 'localStorage' : 'sessionStorage'
      });
      
      toast.success('Oturumunuz başarıyla yenilendi');
    } catch (error) {
      console.error('AuthContext: Failed to refresh session:', error);
      toast.error('Oturum yenilenirken bir hata oluştu');
      // Ciddi bir hata durumunda logout yap
      if (error instanceof Error && error.message.includes('unauthorized')) {
        console.log('AuthContext: Unauthorized error during refresh, logging out');
        await logout();
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Memoized refresh session
  const memoizedRefreshSession = useCallback(refreshSession, []);

  // Oturum süresini kontrol et
  const checkSessionTime = useCallback(() => {
    // isAuthenticated false ise hiçbir şey yapma
    if (!isAuthenticated) {
      console.log('AuthContext: Session time check skipped - not authenticated');
      return;
    }

    console.log('AuthContext: Checking session time');
    
    try {
      // Token'ı al
      const isRememberMe = localStorage.getItem('rememberMe') === 'true';
      const storage = isRememberMe ? localStorage : sessionStorage;
      const token = storage.getItem('token');
      
      if (!token) {
        console.log('AuthContext: No token found, logging out');
        logout();
        return;
      }

      // Token decode et
      const decodedToken: any = jwtDecode(token);
      const exp = decodedToken.exp;
      
      if (!exp) {
        console.log('AuthContext: No expiration in token, logging out');
        logout();
        return;
      }

      // Kalan süreyi hesapla (milisaniye ve saniye cinsinden)
      const now = Date.now();
      const expirationTime = exp * 1000;
      const remainingMs = expirationTime - now;
      const remainingSeconds = Math.floor(remainingMs / 1000);
      
      // Kullanıcı ayarlarından alınan oturum süresini ve kalan süreyi logla
      console.log('AuthContext: Session time check', {
        now: new Date(now).toISOString(),
        exp: new Date(expirationTime).toISOString(),
        remainingMs,
        remainingSeconds: Math.floor(remainingMs / 1000),
        userTimeoutSetting: sessionTimeoutMinutesState
      });

      // Kalan süreyi state'e saniye cinsinden set et
      setRemainingSessionTime(remainingSeconds);
      
      // Oturum süresi dolduysa logout yap
      if (remainingSeconds <= 0 && isAuthenticated) {
        console.log('AuthContext: Session expired, logging out');
        logout();
        toast.info('Oturum süreniz doldu. Lütfen tekrar giriş yapın.', {
          position: 'top-right',
          autoClose: 5000
        });
      }
    } catch (error) {
      console.error('AuthContext: Error checking session time:', error);
    }
  }, [isAuthenticated, showTimeoutWarning, sessionSettingsState.autoRefreshSession, logout, memoizedRefreshSession]);

  // Oturum süresini düzenli olarak kontrol et (10 saniyede bir)
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('AuthContext: Session time check disabled - not authenticated');
      return;
    }
    
    console.log('AuthContext: Setting up session time check interval');
    
    // İlk kontrolü hemen yap
    checkSessionTime();
    
    // 10 saniyede bir kalan süreyi kontrol et
    const timeCheckInterval = setInterval(() => {
      checkSessionTime();
    }, 10000); // 10 saniyede bir
    
    return () => {
      console.log('AuthContext: Cleaning up session time check interval');
      clearInterval(timeCheckInterval);
    };
  }, [isAuthenticated, checkSessionTime]);

  // Session validation - backend ile oturum kontrolü (her 30 saniyede bir)
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('AuthContext: Session validation disabled - not authenticated');
      return;
    }
    
    console.log('AuthContext: Setting up session validation interval');
    
    // Her 30 saniyede bir backend ile oturum kontrolü yap
    const interval = setInterval(async () => {
      try {
        console.log('AuthContext: Validating session with backend');
        // Session validation API call
        // Burada validateSession yerine getCurrentUser kullanıyoruz
        // Çünkü validateSession metodu henüz implement edilmemiş olabilir
        await authApi.getCurrentUser();
        console.log('AuthContext: Session is still valid');

      } catch (error) {
        console.error('AuthContext: Session validation failed:', error);
        // 401 hatası alındığında logout yap
        if (error instanceof Error) {
          // Hata mesajını kontrol et
          const errorMessage = error.message.toLowerCase();
          console.log('AuthContext: Session is no longer valid, logging out');
          await logout();
          
          // Oturum uzaktan kapatıldıysa özel mesaj göster
          if (errorMessage.includes('terminated') || errorMessage.includes('sonlandırıldı')) {
            toast.error('Oturumunuz yönetici tarafından sonlandırıldı. Lütfen tekrar giriş yapın.');
          } 
          // Oturum süresi dolduysa
          else if (errorMessage.includes('expired') || errorMessage.includes('süresi doldu')) {
            toast.error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
          }
          // Genel 401 hatası
          else if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
            toast.error('Oturumunuz sonlandırıldı. Lütfen tekrar giriş yapın.');
          }
        } else {
          // Geçici ağ sorunları olabilir, sadece loglama yap
          console.log('AuthContext: Temporary network issue, not logging out');
        }
      }
    }, 30000); // 30 saniyede bir
    
    return () => {
      console.log('AuthContext: Cleaning up session validation interval');
      clearInterval(interval);
    };
  }, [isAuthenticated, logout]);

  // Context değerini oluştur
  const value = {
    user,
    isAuthenticated,
    isLoading,
    loading,
    error,
    sessionTimeoutMinutes: sessionTimeoutMinutesState,
    setSessionTimeoutMinutes,
    remainingSessionTime,
    showTimeoutWarning,
    timeoutCountdown,
    extendSession,
    sessionSettings: sessionSettingsState,
    setSessionSettings,
    userTimeZone: getUserTimeZone(),
    getUserTimeZone,
    formatDateForUser,
    formatDateTimeForUser,
    login,
    logout,
    refreshSession: memoizedRefreshSession,
    apiUrl
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        loading,
        error,
        sessionTimeoutMinutes: sessionTimeoutMinutesState,
        setSessionTimeoutMinutes,
        remainingSessionTime,
        showTimeoutWarning,
        timeoutCountdown,
        extendSession,
        sessionSettings: sessionSettingsState,
        setSessionSettings,
        userTimeZone: getUserTimeZone(),
        getUserTimeZone,
        formatDateForUser,
        formatDateTimeForUser,
        login,
        logout,
        refreshSession,
        apiUrl
      }}
    >
      {children}
      
      {/* Oturum süresi uyarı modalı */}
      <Modal
        title={null}
        open={showTimeoutWarning}
        closable={false}
        maskClosable={false}
        footer={null}
        centered
        width={400}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <WarningOutlined style={{ fontSize: '48px', color: '#faad14', marginBottom: '16px' }} />
          <Title level={4}>Oturumunuz sona ermek üzere!</Title>
          <Text>
            Oturumunuz <strong>{timeoutCountdown}</strong> saniye içinde sona erecek. Devam etmek istiyor musunuz?
          </Text>
          <div style={{ marginTop: '16px' }}>
            <Text type="secondary" style={{ fontSize: '14px' }}>
              💡 İpucu: Profil sayfasından oturum sürenizi ayarlayabilirsiniz.
            </Text>
          </div>
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <Button type="primary" onClick={extendSession}>
              Oturumu Uzat
            </Button>
            <Button onClick={logout}>
              Çıkış Yap
            </Button>
          </div>
        </div>
      </Modal>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
